import { ethers } from "ethers";
import axios from "axios";
import pinataSDK from "@pinata/sdk";
import fs from "fs";
import { getContracts } from "../utils/getContracts";
import { formatUnits, parseEther } from "ethers";

// Initialize Pinata SDK
const pinata = new pinataSDK({
  pinataJWTKey:
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySW5mb3JtYXRpb24iOnsiaWQiOiI2NDgyZjkxZS0zNjNlLTQ5OWYtYjQ1ZC0wNzNmYzA4NmQxYTUiLCJlbWFpbCI6InRlaGlsYWZhdm91cml0ZUBnbWFpbC5jb20iLCJlbWFpbF92ZXJpZmllZCI6dHJ1ZSwicGluX3BvbGljeSI6eyJyZWdpb25zIjpbeyJpZCI6IkZSQTEiLCJkZXNpcmVkUmVwbGljYXRpb25Db3VudCI6MX1dLCJ2ZXJzaW9uIjoxfSwibWZhX2VuYWJsZWQiOmZhbHNlLCJzdGF0dXMiOiJBQ1RJVkUifSwiYXV0aGVudGljYXRpb25UeXBlIjoic2NvcGVkS2V5Iiwic2NvcGVkS2V5S2V5IjoiNTczMGRiYjgwNjhiZjFjYjdkYTUiLCJzY29wZWRLZXlTZWNyZXQiOiI1MTkyMmJmNDM1ZGUwYWM5YWMwN2UzYmE4NzQ1OGZiYzU5N2FjOTI3MzJhOTJjMGFlNjQyYmQ1OWNhZWRkMzk4IiwiaWF0IjoxNzAwMjA4NzQzfQ.t850Nq1NQGZTnpw-f-NEhVUrk892-UBFUWOp6hkiqnk",
});

const abiCoder = new ethers.AbiCoder();

// Encoding functions
function encodeStaticAttributes(
  facilityName: string,
  facilityDescription: string,
  facilityLocation: string,
  facilityCategory: string,
  userID: string,
  subscriptionPlan: string,
  verifier: string
): string {
  const encodedStaticAttributes = abiCoder.encode(
    ["string", "string", "string", "string", "string", "string", "string"],
    [
      facilityName,
      facilityDescription,
      facilityLocation,
      facilityCategory,
      userID,
      subscriptionPlan,
      verifier,
    ]
  );
  console.log("Encoded Static Attributes:", encodedStaticAttributes);
  return encodedStaticAttributes;
}

function encodeDynamicAttributes(
  reportingPeriod: string,
  directStationaryEmissionsValue: number,
  totalGHGEmissions: number,
  quantifiedGHGEmissionsInBaselineScenario: number,
  facilityCapacity: number
): string {
  const convertedDirectStationaryEmissionsValue = ethers.parseEther(
    directStationaryEmissionsValue.toString()
  );
  const convertedTotalGHGEmissions = ethers.parseEther(
    totalGHGEmissions.toString()
  );
  const convertedQuantifiedGHGEmissionsInBaselineScenario =
    ethers.parseEther(
      quantifiedGHGEmissionsInBaselineScenario.toString()
    );
  const convertedFacilityCapacity = ethers.parseEther(
    facilityCapacity.toString()
  );

  const encodedDynamicAttributes = abiCoder.encode(
    ["string", "uint256", "uint256", "uint256", "uint256"],
    [
      reportingPeriod,
      convertedDirectStationaryEmissionsValue,
      convertedTotalGHGEmissions,
      convertedQuantifiedGHGEmissionsInBaselineScenario,
      convertedFacilityCapacity,
    ]
  );
  console.log("Encoded Dynamic Attributes:", encodedDynamicAttributes);
  return encodedDynamicAttributes;
}

// Role Management Functions
async function grantRole(role: number, account: string) {
  try {
    const { businessCarbonMonitoringContractInstance } = await getContracts();

    let roleBytes32: string;
    switch (role) {
      case 1:
        roleBytes32 = ethers.keccak256(
          ethers.toUtf8Bytes("VALIDATOR_ROLE")
        );
        break;
      case 2:
        roleBytes32 = ethers.keccak256(
          ethers.toUtf8Bytes("VERIFIER_ROLE")
        );
        break;
      case 3:
        roleBytes32 = ethers.keccak256(
          ethers.toUtf8Bytes("ADMIN_ROLE")
        );
        break;
      default:
        throw new Error("Invalid role number");
    }

    const tx = await businessCarbonMonitoringContractInstance.grantRole(
      roleBytes32,
      account,
      true
    );
    await tx.wait();

    return {
      success: true,
      transactionHash: tx.hash,
      message: `Role granted to ${account}`,
    };
  } catch (error: any) {
    console.error("Error granting role:", error);
    return {
      success: false,
      error: error.message || "Unknown error occurred",
    };
  }
}

// Contract Address Management
async function setInfrastructureChangeNFTAddress(address: string) {
  try {
    const { businessCarbonMonitoringContractInstance } = await getContracts();

    const tx =
      await businessCarbonMonitoringContractInstance.setInfrastructureChangeNFTAddress(
        address
      );
    await tx.wait();

    return {
      success: true,
      transactionHash: tx.hash,
      address,
    };
  } catch (error: any) {
    console.error("Error setting Infrastructure Change NFT address:", error);
    return {
      success: false,
      error: error.message || "Unknown error occurred",
    };
  }
}

// KYC and Plant Management Functions
async function performKYC(plantAddress: string, staticAttributes: string) {
  try {
    const { businessCarbonMonitoringContractInstance } = await getContracts();

    const tx = await businessCarbonMonitoringContractInstance.performKYC(
      plantAddress,
      staticAttributes
    );
    await tx.wait();

    return {
      success: true,
      transactionHash: tx.hash,
      plantAddress,
    };
  } catch (error: any) {
    console.error("Error performing KYC:", error);
    return {
      success: false,
      error: error.message || "Unknown error occurred",
    };
  }
}

async function validatePlant(plantAddress: string) {
  try {
    const { businessCarbonMonitoringContractInstance } = await getContracts();

    const tx = await businessCarbonMonitoringContractInstance.validate(
      plantAddress
    );
    await tx.wait();

    return {
      success: true,
      transactionHash: tx.hash,
      plantAddress,
    };
  } catch (error: any) {
    console.error("Error validating plant:", error);
    return {
      success: false,
      error: error.message || "Unknown error occurred",
    };
  }
}

async function recordPlant(
  plantAddresses: string[],
  dynamicAttributes: string[]
) {
  try {
    const { businessCarbonMonitoringContractInstance } = await getContracts();

    const tx = await businessCarbonMonitoringContractInstance.recordPlant(
      plantAddresses,
      dynamicAttributes
    );
    await tx.wait();

    return {
      success: true,
      transactionHash: tx.hash,
      plantsRecorded: plantAddresses.length,
    };
  } catch (error: any) {
    console.error("Error recording plants:", error);
    return {
      success: false,
      error: error.message || "Unknown error occurred",
    };
  }
}

async function verifyAndMint(plantAddress: string) {
  try {
    const { businessCarbonMonitoringContractInstance } = await getContracts();

    const tx = await businessCarbonMonitoringContractInstance.verifyAndMint(
      plantAddress
    );
    await tx.wait();

    return {
      success: true,
      transactionHash: tx.hash,
      plantAddress,
    };
  } catch (error: any) {
    console.error("Error verifying and minting:", error);
    return {
      success: false,
      error: error.message || "Unknown error occurred",
    };
  }
}

// Read Functions
async function getStaticAttributes(plantAddress: string) {
  try {
    const { businessCarbonMonitoringContractInstance } = await getContracts();

    const staticAttributes =
      await businessCarbonMonitoringContractInstance.getStaticAttributes(
        plantAddress
      );

    return {
      facilityName: staticAttributes[0],
      facilityDescription: staticAttributes[1],
      facilityLocation: staticAttributes[2],
      facilityCategory: staticAttributes[3],
      userID: staticAttributes[4],
      subscriptionPlan: staticAttributes[5],
      verifier: staticAttributes[6],
    };
  } catch (error: any) {
    console.error("Error getting static attributes:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getDynamicAttributes(plantAddress: string, index: number) {
  try {
    const { businessCarbonMonitoringContractInstance } = await getContracts();

    const dynamicAttributes =
      await businessCarbonMonitoringContractInstance.dynamicAttributes(
        plantAddress,
        index
      );

    return {
      reportingPeriod: dynamicAttributes.reportingPeriod,
      directStationaryEmissionsValue: ethers.formatEther(
        dynamicAttributes.directStationaryEmissionsValue
      ),
      totalGHGEmissions: ethers.formatEther(
        dynamicAttributes.totalGHGEmissions
      ),
      quantifiedGHGEmissionsInBaselineScenario: ethers.formatEther(
        dynamicAttributes.quantifiedGHGEmissionsInBaselineScenario
      ),
      facilityCapacity: ethers.formatEther(
        dynamicAttributes.facilityCapacity
      ),
    };
  } catch (error: any) {
    console.error("Error getting dynamic attributes:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getAllEmissionValues(plantAddress: string) {
  try {
    const { businessCarbonMonitoringContractInstance } = await getContracts();

    const allEmissionValues =
      await businessCarbonMonitoringContractInstance.allEmissionValues(
        plantAddress
      );

    return {
      reportingPeriod: allEmissionValues.reportingPeriod,
      directStationaryEmissionsValue: ethers.formatEther(
        allEmissionValues.directStationaryEmissionsValue
      ),
      totalGHGEmissions: ethers.formatEther(
        allEmissionValues.totalGHGEmissions
      ),
      quantifiedGHGEmissionsInBaselineScenario: ethers.formatEther(
        allEmissionValues.quantifiedGHGEmissionsInBaselineScenario
      ),
      facilityCapacity: ethers.formatEther(
        allEmissionValues.facilityCapacity
      ),
      isVerified: allEmissionValues.isVerified,
    };
  } catch (error: any) {
    console.error("Error getting all emission values:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getPlantInfo(plantAddress: string) {
  try {
    const { businessCarbonMonitoringContractInstance } = await getContracts();

    const plantInfo = await businessCarbonMonitoringContractInstance.plantInfo(
      plantAddress
    );

    return {
      plantAddress: plantInfo.plantAddress,
      organizationName: plantInfo.organizationName,
      scenario: plantInfo.scenario,
    };
  } catch (error: any) {
    console.error("Error getting plant info:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getPlantEmissionData(plantAddress: string) {
  try {
    const { businessCarbonMonitoringContractInstance } = await getContracts();

    const emissionData =
      await businessCarbonMonitoringContractInstance.getPlantEmissionData(
        plantAddress
      );

    const formattedData = emissionData.map((emission: any) => ({
      reportingPeriod: emission.reportingPeriod,
      directStationaryEmissionsValue: ethers.formatEther(
        emission.directStationaryEmissionsValue
      ),
      totalGHGEmissions: ethers.formatEther(emission.totalGHGEmissions),
      quantifiedGHGEmissionsInBaselineScenario: ethers.formatEther(
        emission.quantifiedGHGEmissionsInBaselineScenario
      ),
      facilityCapacity: ethers.formatEther(emission.facilityCapacity),
      isVerified: emission.isVerified,
    }));

    return {
      plantAddress,
      emissionData: formattedData,
    };
  } catch (error: any) {
    console.error("Error getting plant emission data:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getAllPlantsLength() {
  try {
    const { businessCarbonMonitoringContractInstance } = await getContracts();

    const length =
      await businessCarbonMonitoringContractInstance.getAllPlantsLength();

    return {
      totalPlants: length.toString(),
    };
  } catch (error: any) {
    console.error("Error getting all plants length:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getAllPlantEmissionLength(plantAddress: string) {
  try {
    const { businessCarbonMonitoringContractInstance } = await getContracts();

    const length =
      await businessCarbonMonitoringContractInstance.getAllPlantEmissionLength(
        plantAddress
      );

    return {
      emissionRecords: length.toString(),
    };
  } catch (error: any) {
    console.error("Error getting plant emission length:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getAllUserEmissionLength(plantAddress: string) {
  try {
    const { businessCarbonMonitoringContractInstance } = await getContracts();

    const length =
      await businessCarbonMonitoringContractInstance.allUserEmissionLength(
        plantAddress
      );

    return {
      userEmissionRecords: length.toString(),
    };
  } catch (error: any) {
    console.error("Error getting user emission length:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getAllPlantsLengthDirectCall() {
  try {
    const { businessCarbonMonitoringContractInstance } = await getContracts();

    const length =
      await businessCarbonMonitoringContractInstance.allPlantsLength();

    return {
      totalPlants: length.toString(),
    };
  } catch (error: any) {
    console.error("Error getting all plants length (direct call):", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function getPlantsData(start: number, count: number) {
  try {
    const { businessCarbonMonitoringContractInstance } = await getContracts();

    const plantsData =
      await businessCarbonMonitoringContractInstance.getPlantsData(
        start,
        count
      );

    const formattedData = plantsData.map((plant: any) => ({
      plantAddress: plant.plantAddress,
      organizationName: plant.organizationName,
      scenario: plant.scenario,
      emissionData: {
        reportingPeriod: plant.emissionData.reportingPeriod,
        directStationaryEmissionsValue: ethers.formatEther(
          plant.emissionData.directStationaryEmissionsValue
        ),
        totalGHGEmissions: ethers.formatEther(
          plant.emissionData.totalGHGEmissions
        ),
        quantifiedGHGEmissionsInBaselineScenario: ethers.formatEther(
          plant.emissionData.quantifiedGHGEmissionsInBaselineScenario
        ),
        facilityCapacity: ethers.formatEther(
          plant.emissionData.facilityCapacity
        ),
        isVerified: plant.emissionData.isVerified,
      },
      isKYCPerformed: plant.isKYCPerformed,
      isPlantRecorded: plant.isPlantRecorded,
      isValidated: plant.isValidated,
      isVerified: plant.isVerified,
    }));

    return {
      plantsData: formattedData,
    };
  } catch (error: any) {
    console.error("Error getting plants data:", error);
    return {
      error: error.message || "Unknown error occurred",
    };
  }
}

async function isKYCPerformed(userAddress: string) {
  try {
    const { businessCarbonMonitoringContractInstance } = await getContracts();

    const kycStatus =
      await businessCarbonMonitoringContractInstance.isKYCPerformed(
        userAddress
      );

    return {
      userAddress,
      isKYCPerformed: kycStatus,
    };
  } catch (error) {
    console.error("Error checking KYC status:", error);
    return {
      error: (error as Error).message,
    };
  }
}

// Export all functions
export {
  encodeStaticAttributes,
  encodeDynamicAttributes,
  grantRole,
  setInfrastructureChangeNFTAddress,
  performKYC,
  validatePlant,
  recordPlant,
  verifyAndMint,
  getStaticAttributes,
  getDynamicAttributes,
  getAllEmissionValues,
  getPlantInfo,
  getPlantEmissionData,
  getAllPlantsLength,
  getAllPlantEmissionLength,
  getAllUserEmissionLength,
  getAllPlantsLengthDirectCall,
  getPlantsData,
  isKYCPerformed,
};

// Example usage (commented out)
/*
// Grant admin role
grantRole(3, "0xE05b38e08EA9aC632639deCb31258bDef3533E04")
  .then((result) => {
    console.log("Grant role result:", result);
  })
  .catch((err) => {
    console.log("Grant role error:", err);
  });

// Example static attributes encoding
const facilityName = "Carbon Monitoring Facility";
const facilityDescription = "Industrial facility for carbon emission monitoring";
const facilityLocation = "Lagos, Nigeria";
const facilityCategory = "Carbon Monitoring";
const userID = "user123";
const subscriptionPlan = "Premium";
const verifier = "Verifier1";

const encodedStatic = encodeStaticAttributes(
  facilityName,
  facilityDescription,
  facilityLocation,
  facilityCategory,
  userID,
  subscriptionPlan,
  verifier
);

// Example dynamic attributes encoding
const reportingPeriod = "Q1 2024";
const directStationaryEmissionsValue = 800;
const totalGHGEmissions = 1200;
const quantifiedGHGEmissionsInBaselineScenario = 2000;
const facilityCapacity = 3000;

const encodedDynamic = encodeDynamicAttributes(
  reportingPeriod,
  directStationaryEmissionsValue,
  totalGHGEmissions,
  quantifiedGHGEmissionsInBaselineScenario,
  facilityCapacity
);
*/

// Test function call
getAllPlantsLength()
  .then((result) => {
    console.log("Total plants:", result);
  })
  .catch((err) => {
    console.log("Error getting plants length:", err);
  });
